import { ethers } from "ethers";
import {
  CARBON_V2_OPERATION_TYPES,
  DIRECT_WALLET_CAPABLE_OPERATION_TYPES,
} from "../actions/actionCoverage";
import { encodeCarbonV2BatchInvestmentCalldata } from "../read/v2ReadManifest";

export const EXECUTION_MODES = Object.freeze([
  "METAKEEP_BUSINESS_WALLET",
  "DIRECT_WALLET",
  "BACKEND_MANAGED_WALLET",
  "SAFE_TIMELOCK_EXTERNAL",
]);

export const OPERATION_STATES = Object.freeze([
  "PREPARING",
  "CONSENT_READY",
  "AWAITING_TRANSACTION_ID",
  "READY_TO_SIGN",
  "READY_TO_SUBMIT",
  "SUBMITTED",
  "MINED",
  "VERIFYING",
  "COMPLETED",
  "NEEDS_REVIEW",
  "REJECTED",
  "FAILED",
]);

export const TERMINAL_OPERATION_STATES = Object.freeze([
  "COMPLETED",
  "NEEDS_REVIEW",
  "REJECTED",
  "FAILED",
]);

export const PROJECT_CREATION_OPERATION_TYPE = "V2_CARBON_PROJECT_CREATION";

export const DIRECT_WALLET_OPERATION_TYPES =
  DIRECT_WALLET_CAPABLE_OPERATION_TYPES;

export const RECONCILIATION_STATUSES = Object.freeze([
  "PENDING",
  "COMPLETED",
  "FAILED",
  "NEEDS_REVIEW",
]);

export const FORBIDDEN_AUTHORITY_FIELDS = Object.freeze([
  ["user", "identifier"].join("_"),
  ["original", "address"].join("_"),
  "authWalletAddress",
  "auth_wallet_address",
  "businessWalletAddress",
  "business_wallet_address",
  "personalWallet",
  "personal_wallet",
  "role",
  "chainId",
  "chain_id",
  "target",
  "factory",
  ["func", "tion"].join(""),
  ["selec", "tor"].join(""),
  ["ar", "gs"].join(""),
  ["call", "data"].join(""),
  "nonce",
  "creationId",
  "creation_id",
  "semanticConfigHash",
  "semantic_config_hash",
  "metakeepIdentity",
  "metakeep_identity",
]);

const allowedSafeOperationFields = Object.freeze([
  "operationId",
  "operationType",
  "executionMode",
  "state",
  "protocolVersion",
  "status",
  "creationId",
  "semanticConfigHash",
  "chainId",
  "factoryAddress",
  "businessWalletAddress",
  "expectedOnchainActor",
  "projectAddress",
  "receiptAddress",
  "settlementVaultAddress",
  "calldataHash",
  "transactionRequestHash",
  "transactionHash",
  "blockNumber",
  "vendorStatus",
  "protocolPostconditionStatus",
  "postconditionStatus",
  "normalizedErrorCode",
  "normalizedErrorMessage",
  "applicationProjectionStatus",
  "createdAt",
  "updatedAt",
  "submittedAt",
  "completedAt",
  "metakeepTransactionId",
]);

const responseAliasFields = Object.freeze({
  allowed_buyer: "allowedBuyer",
  application_projection_status: "applicationProjectionStatus",
  allowance_required: "allowanceRequired",
  amount_type: "amountType",
  approval_required: "approvalRequired",
  business_wallet_address: "businessWalletAddress",
  block_number: "blockNumber",
  calldata_hash: "calldataHash",
  chain_id: "chainId",
  completed_at: "completedAt",
  contracts_release_commit: "contractsReleaseCommit",
  consent_token: "consentToken",
  created_at: "createdAt",
  creation_id: "creationId",
  execution_mode: "executionMode",
  effective_amount: "effectiveAmount",
  estimated_gas: "estimatedGas",
  expires_at: "expiresAt",
  expected_onchain_actor: "expectedOnchainActor",
  factory_address: "factoryAddress",
  listing_id: "listingId",
  marketplace_address: "marketplaceAddress",
  metakeep_transaction_id: "metakeepTransactionId",
  gas_limit: "gasLimit",
  gas_upper_bound: "gasUpperBound",
  investment_plan: "investmentPlan",
  issued_units_gross: "issuedUnitsGross",
  limited_by_gas: "limitedByGas",
  next_listing_id: "nextListingId",
  normalized_error_code: "normalizedErrorCode",
  normalized_error_message: "normalizedErrorMessage",
  net_principal: "netPrincipal",
  operation_id: "operationId",
  operation_state: "operationState",
  operation_type: "operationType",
  plan_hash: "planHash",
  pending_balances: "pendingBalances",
  pending_units: "pendingUnits",
  owner_address: "ownerAddress",
  project_readiness: "projectReadiness",
  postcondition_status: "postconditionStatus",
  project_address: "projectAddress",
  protocol_postcondition_status: "protocolPostconditionStatus",
  protocol_version: "protocolVersion",
  receipt_address: "receiptAddress",
  receipt_id: "receiptId",
  receipt_token_fee_bps: "receiptTokenFeeBps",
  treasury_receipt_fee_units: "treasuryReceiptFeeUnits",
  receipt_token_id: "receiptTokenId",
  release_tag: "releaseTag",
  requested_amount: "requestedAmount",
  required_amount: "requiredAmount",
  semantic_config_hash: "semanticConfigHash",
  settlement_vault_address: "settlementVaultAddress",
  spender_address: "spenderAddress",
  stablecoin_address: "stablecoinAddress",
  stablecoin_decimals: "stablecoinDecimals",
  stage_index: "stageIndex",
  stage_allocation: "stageAllocation",
  stablecoin_fee: "stablecoinFee",
  stablecoin_fee_bps: "stablecoinFeeBps",
  stablecoin_paid_gross: "stablecoinPaidGross",
  snapshot_block_hash: "snapshotBlockHash",
  snapshot_block_number: "snapshotBlockNumber",
  claimable_amount: "claimableAmount",
  compensation_paid: "compensationPaid",
  can_claim: "canClaim",
  submitted_at: "submittedAt",
  transaction_hash: "transactionHash",
  transaction_request: "transactionRequest",
  transaction_request_hash: "transactionRequestHash",
  total_receipt_units: "totalReceiptUnits",
  total_stable_paid: "totalStablePaid",
  unallocated_amount: "unallocatedAmount",
  unit_price: "unitPrice",
  units_remaining: "unitsRemaining",
  units_sold: "unitsSold",
  updated_at: "updatedAt",
  vendor_status: "vendorStatus",
  wallet_eligibility: "walletEligibility",
  indexed_state: "indexedState",
});

const assertNoResponseAliasFields = (value, label) => {
  for (const key of Object.keys(value)) {
    const canonical = responseAliasFields[key];
    if (canonical) {
      throw new Error(`${label} must use ${canonical}, not ${key}`);
    }
  }
};

const assertPlainObject = (value, label) => {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    throw new Error(`${label} must be an object`);
  }
};

export const assertNoForbiddenAuthorityFields = (value, path = "request") => {
  if (!value || typeof value !== "object") {
    return;
  }
  if (Array.isArray(value)) {
    value.forEach((entry, index) =>
      assertNoForbiddenAuthorityFields(entry, `${path}[${index}]`)
    );
    return;
  }
  for (const key of Object.keys(value)) {
    if (FORBIDDEN_AUTHORITY_FIELDS.includes(key)) {
      throw new Error(`${path}.${key} is not accepted by current Carbon APIs`);
    }
    assertNoForbiddenAuthorityFields(value[key], `${path}.${key}`);
  }
};

export const sanitizeRequestBody = (body, allowedFields) => {
  assertPlainObject(body, "request body");
  assertNoForbiddenAuthorityFields(body);
  const allowed = new Set(allowedFields);
  const sanitized = {};
  for (const [key, value] of Object.entries(body)) {
    if (!allowed.has(key)) {
      throw new Error(`request body.${key} is not accepted by this endpoint`);
    }
    sanitized[key] = value;
  }
  return sanitized;
};

export const normalizeProtocolOperationResponse = (raw) => {
  assertPlainObject(raw, "operation response");
  assertNoResponseAliasFields(raw, "operation response");
  const operationId = raw.operationId;
  const operationType = raw.operationType;
  const executionMode = raw.executionMode;
  const state = raw.state;
  if (!operationId) {
    throw new Error("operation response missing operationId");
  }
  if (!operationType) {
    throw new Error("operation response missing operationType");
  }
  if (!EXECUTION_MODES.includes(executionMode)) {
    throw new Error(`unknown execution mode: ${executionMode}`);
  }
  if (!OPERATION_STATES.includes(state)) {
    throw new Error(`unknown operation state: ${state}`);
  }
  if (
    raw.blockNumber !== undefined &&
    raw.blockNumber !== null &&
    (!Number.isSafeInteger(raw.blockNumber) || raw.blockNumber < 0)
  ) {
    throw new Error("operation response blockNumber must be a non-negative safe integer");
  }

  const normalized = {
    operationId,
    operationType,
    executionMode,
    state,
  };
  for (const field of allowedSafeOperationFields) {
    const value = raw[field];
    if (value !== undefined) {
      normalized[field] = value;
    }
  }
  return normalized;
};

export const normalizePreparedOperationReference = (raw) => {
  assertPlainObject(raw, "prepared operation response");
  assertNoResponseAliasFields(raw, "prepared operation response");
  if (raw.operationState === undefined) {
    return normalizeProtocolOperationResponse(raw);
  }
  if (raw.state !== undefined) {
    throw new Error("prepare response must use operationState");
  }
  return normalizeProtocolOperationResponse({
    ...raw,
    state: raw.operationState,
  });
};

export const normalizeCreationPrepareResponse = (raw) => {
  const normalized = normalizePrepareResponse(raw);
  if (normalized.operationType !== PROJECT_CREATION_OPERATION_TYPE) {
    throw new Error(
      `project creation prepare expected ${PROJECT_CREATION_OPERATION_TYPE}`
    );
  }
  return normalized;
};

export const normalizeReconciliationResult = (raw) => {
  assertPlainObject(raw, "reconciliation response");
  assertNoResponseAliasFields(raw, "reconciliation response");
  const operationId = raw.operationId;
  const status = raw.status;
  if (!operationId) {
    throw new Error("reconciliation response missing operationId");
  }
  if (!RECONCILIATION_STATUSES.includes(status)) {
    throw new Error(`unknown reconciliation status: ${status}`);
  }
  const result = { operationId, status };
  const applicationProjectionStatus = raw.applicationProjectionStatus;
  if (applicationProjectionStatus !== undefined && applicationProjectionStatus !== null) {
    result.applicationProjectionStatus = applicationProjectionStatus;
  }
  return result;
};

const normalizeUintString = (value, label) => {
  if (typeof value !== "string" || !/^(0|[1-9]\d*)$/.test(value)) {
    throw new Error(`${label} must be an integer string`);
  }
  return value;
};

const UINT256_MAX = (1n << 256n) - 1n;
const UINT128_MAX = (1n << 128n) - 1n;

const normalizeBoundedUintString = (
  value,
  label,
  { max = UINT256_MAX, allowZero = true } = {}
) => {
  const normalized = normalizeUintString(value, label);
  const parsed = BigInt(normalized);
  if ((!allowZero && parsed === 0n) || parsed > max) {
    throw new Error(`${label} is out of range`);
  }
  return normalized;
};

const normalizeAddress = (value, label) => {
  try {
    return ethers.utils.getAddress(value);
  } catch {
    throw new Error(`${label} must be a valid address`);
  }
};

const normalizeAddressLower = (value, label) =>
  normalizeAddress(value, label).toLowerCase();

const normalizeBytes32 = (value, label) => {
  if (typeof value !== "string" || !/^0x[0-9a-fA-F]{64}$/.test(value)) {
    throw new Error(`${label} must be a bytes32 hex string`);
  }
  return value.toLowerCase();
};

const assertExactFields = (value, label, requiredFields) => {
  assertPlainObject(value, label);
  assertNoResponseAliasFields(value, label);
  const expected = new Set(requiredFields);
  for (const key of Object.keys(value)) {
    if (!expected.has(key)) {
      throw new Error(`${label}.${key} is not accepted`);
    }
  }
  for (const key of requiredFields) {
    if (value[key] === undefined || value[key] === null) {
      throw new Error(`${label}.${key} is required`);
    }
  }
};

const assertExactKeys = (value, label, fields) => {
  assertPlainObject(value, label);
  assertNoResponseAliasFields(value, label);
  const expected = new Set(fields);
  for (const key of Object.keys(value)) {
    if (!expected.has(key)) {
      throw new Error(`${label}.${key} is not accepted`);
    }
  }
  for (const key of fields) {
    if (!Object.prototype.hasOwnProperty.call(value, key)) {
      throw new Error(`${label}.${key} is required`);
    }
  }
};

const normalizeSafePositiveInteger = (value, label) => {
  if (!Number.isSafeInteger(value) || value <= 0) {
    throw new Error(`${label} must be a positive safe integer`);
  }
  return value;
};

const normalizeReleaseText = (value, label) => {
  if (typeof value !== "string" || !value.trim()) {
    throw new Error(`${label} must be a non-empty string`);
  }
  return value;
};

const normalizeReleaseTag = (value, label) => {
  const normalized = normalizeReleaseText(value, label);
  if (normalized !== "carbon-protocol-v2-2.0.0") {
    throw new Error(`${label} is invalid`);
  }
  return normalized;
};

const normalizeReleaseCommit = (value, label) => {
  const normalized = normalizeReleaseText(value, label);
  if (!/^(?:working-tree-)?[0-9a-f]{40}$/.test(normalized)) {
    throw new Error(
      `${label} must be a lowercase 40-character commit or working-tree identity`
    );
  }
  return normalized;
};

const BATCH_INVESTMENT_PLAN_COMMON_FIELDS = Object.freeze([
  "snapshotBlockNumber",
  "snapshotBlockHash",
  "amountType",
  "requestedAmount",
  "effectiveAmount",
  "legs",
  "totalStablePaid",
  "totalReceiptUnits",
  "unallocatedAmount",
  "gasLimit",
  "limitedByGas",
  "allowanceRequired",
]);

const BATCH_INVESTMENT_LEG_FIELDS = Object.freeze([
  "receiptTokenId",
  "year",
  "stage",
  "units",
  "stablecoinPaidGross",
  "stablecoinFee",
  "netPrincipal",
  "issuedUnitsGross",
  "treasuryReceiptFeeUnits",
  "investorIssuedUnits",
  "stablecoinFeeBps",
  "receiptTokenFeeBps",
]);

const MAX_BATCH_INVESTMENT_LEGS = 60;
const MAX_BATCH_INVESTMENT_GAS = 2_000_000n;

const encodeInvestmentPlanBatchCalldata = (investmentPlan) =>
  encodeCarbonV2BatchInvestmentCalldata(investmentPlan.legs);

export const normalizeInvestmentPlan = (raw, { phase = "quote" } = {}) => {
  if (!["quote", "prepare"].includes(phase)) {
    throw new Error("investment plan phase is invalid");
  }
  const phaseGasField = phase === "quote" ? "gasUpperBound" : "estimatedGas";
  const label = "investmentPlan";
  assertExactFields(raw, label, [
    ...BATCH_INVESTMENT_PLAN_COMMON_FIELDS,
    phaseGasField,
  ]);
  if (
    !Array.isArray(raw.legs) ||
    raw.legs.length < 1 ||
    raw.legs.length > MAX_BATCH_INVESTMENT_LEGS
  ) {
    throw new Error(
      `${label}.legs must contain between 1 and ${MAX_BATCH_INVESTMENT_LEGS} entries`
    );
  }
  if (!["stable", "receipt_units"].includes(raw.amountType)) {
    throw new Error(`${label}.amountType is invalid`);
  }
  if (typeof raw.limitedByGas !== "boolean") {
    throw new Error(`${label}.limitedByGas must be boolean`);
  }

  let previousTokenId = -1n;
  let summedUnits = 0n;
  let summedStable = 0n;
  const legs = raw.legs.map((leg, index) => {
    const legLabel = `${label}.legs[${index}]`;
    assertExactFields(leg, legLabel, BATCH_INVESTMENT_LEG_FIELDS);
    const year = normalizeSafePositiveInteger(leg.year, `${legLabel}.year`);
    if (year > 20) {
      throw new Error(`${legLabel}.year is out of range`);
    }
    if (!Number.isSafeInteger(leg.stage) || leg.stage < 0 || leg.stage > 14) {
      throw new Error(`${legLabel}.stage is out of range`);
    }
    const receiptTokenId = normalizeBoundedUintString(
      leg.receiptTokenId,
      `${legLabel}.receiptTokenId`
    );
    const tokenId = BigInt(receiptTokenId);
    const canonicalTokenId = (BigInt(year) << 8n) | BigInt(leg.stage);
    if (tokenId !== canonicalTokenId) {
      throw new Error(`${legLabel}.receiptTokenId is not canonical`);
    }
    if (tokenId <= previousTokenId) {
      throw new Error(`${label}.legs must be canonically ordered`);
    }
    previousTokenId = tokenId;
    const units = normalizeBoundedUintString(leg.units, `${legLabel}.units`, {
      max: UINT128_MAX,
      allowZero: false,
    });
    const stablecoinPaidGross = normalizeBoundedUintString(
      leg.stablecoinPaidGross,
      `${legLabel}.stablecoinPaidGross`,
      { allowZero: false }
    );
    const stablecoinFee = normalizeBoundedUintString(
      leg.stablecoinFee,
      `${legLabel}.stablecoinFee`
    );
    const netPrincipal = normalizeBoundedUintString(
      leg.netPrincipal,
      `${legLabel}.netPrincipal`
    );
    const issuedUnitsGross = normalizeBoundedUintString(
      leg.issuedUnitsGross,
      `${legLabel}.issuedUnitsGross`,
      { max: UINT128_MAX, allowZero: false }
    );
    const treasuryReceiptFeeUnits = normalizeBoundedUintString(
      leg.treasuryReceiptFeeUnits,
      `${legLabel}.treasuryReceiptFeeUnits`,
      { max: UINT128_MAX }
    );
    const investorIssuedUnits = normalizeBoundedUintString(
      leg.investorIssuedUnits,
      `${legLabel}.investorIssuedUnits`,
      { max: UINT128_MAX }
    );
    for (const field of ["stablecoinFeeBps", "receiptTokenFeeBps"]) {
      if (
        !Number.isSafeInteger(leg[field]) ||
        leg[field] < 0 ||
        leg[field] > 3000
      ) {
        throw new Error(`${legLabel}.${field} is out of range`);
      }
    }
    if (
      BigInt(stablecoinFee) + BigInt(netPrincipal) !==
      BigInt(stablecoinPaidGross)
    ) {
      throw new Error(
        `${legLabel} stablecoin fee and principal do not equal stablecoinPaidGross`
      );
    }
    if (BigInt(units) !== BigInt(issuedUnitsGross)) {
      throw new Error(`${legLabel}.units must equal issuedUnitsGross`);
    }
    if (
      BigInt(treasuryReceiptFeeUnits) + BigInt(investorIssuedUnits) !==
      BigInt(issuedUnitsGross)
    ) {
      throw new Error(
        `${legLabel} receipt allocations do not equal issuedUnitsGross`
      );
    }
    summedUnits += BigInt(units);
    summedStable += BigInt(stablecoinPaidGross);
    return Object.freeze({
      receiptTokenId,
      year,
      stage: leg.stage,
      units,
      stablecoinPaidGross,
      stablecoinFee,
      netPrincipal,
      issuedUnitsGross,
      treasuryReceiptFeeUnits,
      investorIssuedUnits,
      stablecoinFeeBps: leg.stablecoinFeeBps,
      receiptTokenFeeBps: leg.receiptTokenFeeBps,
    });
  });

  const requestedAmount = normalizeBoundedUintString(
    raw.requestedAmount,
    `${label}.requestedAmount`,
    { allowZero: false }
  );
  const effectiveAmount = normalizeBoundedUintString(
    raw.effectiveAmount,
    `${label}.effectiveAmount`,
    { allowZero: false }
  );
  const totalStablePaid = normalizeBoundedUintString(
    raw.totalStablePaid,
    `${label}.totalStablePaid`,
    { allowZero: false }
  );
  const totalReceiptUnits = normalizeBoundedUintString(
    raw.totalReceiptUnits,
    `${label}.totalReceiptUnits`,
    { allowZero: false }
  );
  const unallocatedAmount = normalizeBoundedUintString(
    raw.unallocatedAmount,
    `${label}.unallocatedAmount`
  );
  const allowanceRequired = normalizeBoundedUintString(
    raw.allowanceRequired,
    `${label}.allowanceRequired`,
    { allowZero: false }
  );
  if (BigInt(totalStablePaid) !== summedStable) {
    throw new Error(`${label}.totalStablePaid must equal the leg sum`);
  }
  if (BigInt(totalReceiptUnits) !== summedUnits) {
    throw new Error(`${label}.totalReceiptUnits must equal the leg sum`);
  }
  const expectedEffective =
    raw.amountType === "stable" ? totalStablePaid : totalReceiptUnits;
  if (effectiveAmount !== expectedEffective) {
    throw new Error(`${label}.effectiveAmount does not match amountType totals`);
  }
  if (
    BigInt(requestedAmount) !==
    BigInt(effectiveAmount) + BigInt(unallocatedAmount)
  ) {
    throw new Error(`${label}.requestedAmount does not balance with unallocatedAmount`);
  }
  if (allowanceRequired !== totalStablePaid) {
    throw new Error(`${label}.allowanceRequired must equal totalStablePaid`);
  }

  const gasLimit = normalizeBoundedUintString(
    raw.gasLimit,
    `${label}.gasLimit`,
    { allowZero: false }
  );
  const gasValue = normalizeBoundedUintString(
    raw[phaseGasField],
    `${label}.${phaseGasField}`,
    { allowZero: false }
  );
  if (
    BigInt(gasLimit) === 0n ||
    BigInt(gasLimit) > MAX_BATCH_INVESTMENT_GAS
  ) {
    throw new Error(`${label}.gasLimit must be between 1 and 2000000`);
  }
  if (BigInt(gasValue) === 0n || BigInt(gasValue) > BigInt(gasLimit)) {
    throw new Error(`${label}.${phaseGasField} must be positive and not exceed gasLimit`);
  }
  const snapshotBlockHash = normalizeBytes32(
    raw.snapshotBlockHash,
    `${label}.snapshotBlockHash`
  );
  if (snapshotBlockHash === `0x${"00".repeat(32)}`) {
    throw new Error(`${label}.snapshotBlockHash must be nonzero`);
  }
  return Object.freeze({
    snapshotBlockNumber: normalizeBoundedUintString(
      raw.snapshotBlockNumber,
      `${label}.snapshotBlockNumber`
    ),
    snapshotBlockHash,
    amountType: raw.amountType,
    requestedAmount,
    effectiveAmount,
    legs: Object.freeze(legs),
    totalStablePaid,
    totalReceiptUnits,
    unallocatedAmount,
    [phaseGasField]: gasValue,
    gasLimit,
    limitedByGas: raw.limitedByGas,
    allowanceRequired,
  });
};

const BATCH_INVESTMENT_AUTHORITY_FIELDS = Object.freeze([
  "chainId",
  "projectAddress",
  "businessWalletAddress",
  "releaseTag",
  "contractsReleaseCommit",
  "investmentPlan",
]);

const normalizeBatchInvestmentAuthority = (raw, phase) => ({
  chainId: normalizeBoundedUintString(raw.chainId, "chainId", {
    allowZero: false,
  }),
  projectAddress: normalizeAddress(raw.projectAddress, "projectAddress"),
  businessWalletAddress: normalizeAddress(
    raw.businessWalletAddress,
    "businessWalletAddress"
  ),
  releaseTag: normalizeReleaseTag(raw.releaseTag, "releaseTag"),
  contractsReleaseCommit: normalizeReleaseCommit(
    raw.contractsReleaseCommit,
    "contractsReleaseCommit"
  ),
  investmentPlan: normalizeInvestmentPlan(raw.investmentPlan, { phase }),
});

export const normalizeBatchInvestmentQuoteResponse = (raw) => {
  assertExactFields(
    raw,
    "batch investment quote response",
    [...BATCH_INVESTMENT_AUTHORITY_FIELDS, "quoteHash"]
  );
  const quoteHash = normalizeBytes32(raw.quoteHash, "quoteHash");
  if (quoteHash === `0x${"00".repeat(32)}`) {
    throw new Error("quoteHash must be nonzero");
  }
  return Object.freeze({
    ...normalizeBatchInvestmentAuthority(raw, "quote"),
    quoteHash,
  });
};

export const normalizeBatchInvestmentPrepareResponse = (raw) => {
  const label = "batch investment prepare response";
  const requiredFields = [
    "operationId",
    "operationType",
    "operationState",
    "executionMode",
    "businessWalletAddress",
    "expectedOnchainActor",
    "targetAddress",
    "functionSignature",
    "calldataHash",
    "transactionRequestHash",
    "chainId",
    "projectAddress",
    "receiptAddress",
    "settlementVaultAddress",
    "releaseTag",
    "contractsReleaseCommit",
    "investmentPlan",
    "from",
    "to",
    "value",
    "transactionRequest",
  ];
  assertExactFields(raw, label, requiredFields);
  if (!/^v2-op-[0-9a-f]{32}$/.test(raw.operationId)) {
    throw new Error(`${label}.operationId is invalid`);
  }
  if (raw.operationType !== "V2_CARBON_INVESTMENT") {
    throw new Error(`${label}.operationType must be V2_CARBON_INVESTMENT`);
  }
  if (raw.executionMode !== "DIRECT_WALLET") {
    throw new Error(`${label} must be a direct-wallet operation`);
  }
  if (
    ![
      "PREPARING",
      "READY_TO_SIGN",
      "SUBMITTED",
      "MINED",
      "VERIFYING",
      "COMPLETED",
      "FAILED",
      "NEEDS_REVIEW",
    ].includes(raw.operationState)
  ) {
    throw new Error(`${label}.operationState is invalid`);
  }
  if (raw.functionSignature !== "investBatch(bytes)") {
    throw new Error(`${label}.functionSignature is invalid`);
  }
  const authority = normalizeBatchInvestmentAuthority(raw, "prepare");
  const businessWalletAddress = normalizeAddress(
    raw.businessWalletAddress,
    `${label}.businessWalletAddress`
  );
  const expectedOnchainActor = normalizeAddress(
    raw.expectedOnchainActor,
    `${label}.expectedOnchainActor`
  );
  const targetAddress = normalizeAddress(raw.targetAddress, `${label}.targetAddress`);
  const from = normalizeAddress(raw.from, `${label}.from`);
  const to = normalizeAddress(raw.to, `${label}.to`);
  if (
    businessWalletAddress !== expectedOnchainActor ||
    businessWalletAddress !== from
  ) {
    throw new Error(`${label} direct-wallet actor fields do not match`);
  }
  if (targetAddress !== authority.projectAddress || targetAddress !== to) {
    throw new Error(`${label} target fields do not match projectAddress`);
  }
  if (raw.value !== "0") {
    throw new Error(`${label}.value must be zero`);
  }
  const transactionRequest = normalizeBatchTransactionRequest(
    raw.transactionRequest,
    label
  );
  if (
    transactionRequest.chainId !== authority.chainId ||
    transactionRequest.from !== from ||
    transactionRequest.to !== to ||
    transactionRequest.value !== raw.value
  ) {
    throw new Error(`${label}.transactionRequest bindings do not match`);
  }
  if (
    transactionRequest.data !==
    encodeInvestmentPlanBatchCalldata(authority.investmentPlan)
  ) {
    throw new Error(
      `${label}.transactionRequest.data does not encode investmentPlan`
    );
  }
  const calldataHash = normalizeBytes32(raw.calldataHash, `${label}.calldataHash`);
  if (calldataHash !== ethers.utils.keccak256(transactionRequest.data)) {
    throw new Error(`${label}.calldataHash mismatch`);
  }
  const transactionRequestHashValue = normalizeBytes32(
    raw.transactionRequestHash,
    `${label}.transactionRequestHash`
  );
  const expectedTransactionRequestHash = transactionRequestHash(
    transactionRequest
  );
  if (transactionRequestHashValue !== expectedTransactionRequestHash) {
    throw new Error(`${label}.transactionRequestHash mismatch`);
  }
  if (
    BigInt(transactionRequest.gas) !== BigInt(authority.investmentPlan.gasLimit)
  ) {
    throw new Error(`${label}.transactionRequest.gas must equal investmentPlan.gasLimit`);
  }
  return Object.freeze({
    operationId: raw.operationId,
    operationType: "V2_CARBON_INVESTMENT",
    state: raw.operationState,
    executionMode: "DIRECT_WALLET",
    businessWalletAddress,
    expectedOnchainActor,
    targetAddress,
    functionSignature: raw.functionSignature,
    calldataHash,
    transactionRequestHash: transactionRequestHashValue,
    ...authority,
    receiptAddress: normalizeAddress(raw.receiptAddress, `${label}.receiptAddress`),
    settlementVaultAddress: normalizeAddress(
      raw.settlementVaultAddress,
      `${label}.settlementVaultAddress`
    ),
    from,
    to,
    value: "0",
    transactionRequest,
  });
};

const READINESS_STATUSES = Object.freeze(["ready", "blocked", "pending"]);
const ACTION_ELIGIBILITY_STATUSES = Object.freeze([
  "eligible",
  "blocked",
  "pending",
]);
const CERTIFIER_INTERACTION_MODES = Object.freeze([
  "auditor_required",
  "backend_api",
  "onchain",
]);
const CERTIFIER_ACTION_EXECUTION_MODES = Object.freeze([
  "DIRECT_WALLET",
  "BACKEND_MANAGED_WALLET",
  "EXTERNAL_ONCHAIN",
]);

const normalizeReadinessBlockers = (raw, label) => {
  if (!Array.isArray(raw)) {
    throw new Error(`${label} must be an array`);
  }
  return raw.map((blocker, index) => {
    const itemLabel = `${label}[${index}]`;
    assertPlainObject(blocker, itemLabel);
    assertNoResponseAliasFields(blocker, itemLabel);
    if (typeof blocker.code !== "string" || !blocker.code) {
      throw new Error(`${itemLabel}.code is required`);
    }
    if (!["stable", "pending"].includes(blocker.classification)) {
      throw new Error(`${itemLabel}.classification is invalid`);
    }
    const value = {
      code: blocker.code,
      classification: blocker.classification,
    };
    if (blocker.field !== undefined && blocker.field !== null) {
      if (typeof blocker.field !== "string" || !blocker.field) {
        throw new Error(`${itemLabel}.field is invalid`);
      }
      value.field = blocker.field;
    }
    return value;
  });
};

export const normalizeProjectReadinessResponse = (raw) => {
  assertPlainObject(raw, "project readiness response");
  assertNoResponseAliasFields(raw, "project readiness response");
  if (raw.protocolVersion !== "V2") {
    throw new Error("project readiness response.protocolVersion must be V2");
  }
  const projectReadiness = raw.projectReadiness;
  const walletEligibility = raw.walletEligibility;
  assertPlainObject(projectReadiness, "projectReadiness");
  assertPlainObject(walletEligibility, "walletEligibility");
  assertNoResponseAliasFields(projectReadiness, "projectReadiness");
  assertNoResponseAliasFields(walletEligibility, "walletEligibility");
  if (!READINESS_STATUSES.includes(projectReadiness.status)) {
    throw new Error("projectReadiness.status is invalid");
  }
  for (const field of [
    "authorityComplete",
    "authorityFresh",
    "fundingReady",
    "certifierStageReady",
  ]) {
    if (typeof projectReadiness[field] !== "boolean") {
      throw new Error(`projectReadiness.${field} must be boolean`);
    }
  }
  if (walletEligibility.eligibilityScope !== "indexedCommon") {
    throw new Error("walletEligibility.eligibilityScope is invalid");
  }
  assertPlainObject(walletEligibility.actions, "walletEligibility.actions");
  const expectedActionTypes = new Set(CARBON_V2_OPERATION_TYPES);
  const receivedActionTypes = Object.keys(walletEligibility.actions);
  const missingActionTypes = CARBON_V2_OPERATION_TYPES.filter(
    (operationType) => !receivedActionTypes.includes(operationType)
  );
  const unexpectedActionTypes = receivedActionTypes.filter(
    (operationType) => !expectedActionTypes.has(operationType)
  );
  if (missingActionTypes.length || unexpectedActionTypes.length) {
    throw new Error(
      [
        "walletEligibility.actions does not match the frozen Carbon V2 action catalog",
        missingActionTypes.length
          ? `missing: ${missingActionTypes.join(", ")}`
          : null,
        unexpectedActionTypes.length
          ? `unexpected: ${unexpectedActionTypes.join(", ")}`
          : null,
      ]
        .filter(Boolean)
        .join("; ")
    );
  }
  const blockNumber = (value, label) =>
    value === null ? null : normalizeUintString(value, label);
  const actions = {};
  for (const [operationType, action] of Object.entries(
    walletEligibility.actions
  )) {
    const label = `walletEligibility.actions.${operationType}`;
    if (!/^V2_[A-Z0-9_]+$/.test(operationType)) {
      throw new Error(`${label} operation type is invalid`);
    }
    assertPlainObject(action, label);
    assertNoResponseAliasFields(action, label);
    if (!ACTION_ELIGIBILITY_STATUSES.includes(action.status)) {
      throw new Error(`${label}.status is invalid`);
    }
    if (
      action.eligibilityScope !== "indexedCommon" ||
      action.parameterSpecificPreflightRequired !== true ||
      action.transactionSimulationRequired !== true ||
      action.raceResolution !== "refreshAndPrepareAgain" ||
      typeof action.raceNotes !== "string"
    ) {
      throw new Error(`${label} contract is invalid`);
    }
    actions[operationType] = {
      status: action.status,
      blockers: normalizeReadinessBlockers(action.blockers, `${label}.blockers`),
      eligibilityScope: action.eligibilityScope,
      parameterSpecificPreflightRequired: true,
      transactionSimulationRequired: true,
      raceResolution: action.raceResolution,
      raceNotes: action.raceNotes,
    };
    if (action.interactionMode !== undefined && action.interactionMode !== null) {
      if (!CERTIFIER_INTERACTION_MODES.includes(action.interactionMode)) {
        throw new Error(`${label}.interactionMode is invalid`);
      }
      if (!CERTIFIER_ACTION_EXECUTION_MODES.includes(action.executionMode)) {
        throw new Error(`${label}.executionMode is invalid`);
      }
      for (const field of [
        "frontendMayRequestTransaction",
        "backendManagedMaySubmit",
        "externalEventOnly",
      ]) {
        if (typeof action[field] !== "boolean") {
          throw new Error(`${label}.${field} must be boolean`);
        }
      }
      actions[operationType] = {
        ...actions[operationType],
        requiredActor: normalizeAddressLower(
          action.requiredActor,
          `${label}.requiredActor`
        ),
        interactionMode: action.interactionMode,
        executionMode: action.executionMode,
        frontendMayRequestTransaction: action.frontendMayRequestTransaction,
        backendManagedMaySubmit: action.backendManagedMaySubmit,
        externalEventOnly: action.externalEventOnly,
        transactionState:
          action.transactionState === null
            ? null
            : normalizeReleaseText(
                action.transactionState,
                `${label}.transactionState`
              ),
        confirmedBlock: blockNumber(
          action.confirmedBlock,
          `${label}.confirmedBlock`
        ),
      };
    }
  }
  const certifierIntegration = normalizeCertifierIntegration(
    raw.certifierIntegration
  );
  const lifecycle = normalizeLifecycle(raw.lifecycle);
  return {
    protocolVersion: "V2",
    chainId: normalizeUintString(raw.chainId, "chainId"),
    projectAddress: normalizeAddressLower(raw.projectAddress, "projectAddress"),
    projectReadiness: {
      status: projectReadiness.status,
      authorityComplete: projectReadiness.authorityComplete,
      authorityFresh: projectReadiness.authorityFresh,
      indexedThroughBlock: blockNumber(
        projectReadiness.indexedThroughBlock,
        "projectReadiness.indexedThroughBlock"
      ),
      chainHeadBlock: blockNumber(
        projectReadiness.chainHeadBlock,
        "projectReadiness.chainHeadBlock"
      ),
      fundingReady: projectReadiness.fundingReady,
      certifierStageReady: projectReadiness.certifierStageReady,
      blockers: normalizeReadinessBlockers(
        projectReadiness.blockers,
        "projectReadiness.blockers"
      ),
      fundingBlockers: normalizeReadinessBlockers(
        projectReadiness.fundingBlockers,
        "projectReadiness.fundingBlockers"
      ),
      certifierStageBlockers: normalizeReadinessBlockers(
        projectReadiness.certifierStageBlockers,
        "projectReadiness.certifierStageBlockers"
      ),
    },
    walletEligibility: {
      walletAddress: normalizeAddressLower(
        walletEligibility.walletAddress,
        "walletEligibility.walletAddress"
      ),
      role: String(walletEligibility.role || ""),
      eligibilityScope: "indexedCommon",
      actions,
    },
    indexedState: raw.indexedState ?? null,
    certifierIntegration,
    lifecycle,
  };
};

const normalizeNullableText = (value, label) => {
  if (value === null) return null;
  return normalizeReleaseText(value, label);
};

const normalizeCertifierIntegration = (raw) => {
  const label = "certifierIntegration";
  assertExactKeys(raw, label, [
    "status",
    "catalogKey",
    "interactionMode",
    "actionExecutionOwner",
    "requiredOnchainCaller",
    "certifierCodeHash",
    "lastRequest",
    "lastExternalAction",
  ]);
  if (!["FROZEN", "NEEDS_REVIEW"].includes(raw.status)) {
    throw new Error(`${label}.status is invalid`);
  }
  if (!CERTIFIER_INTERACTION_MODES.includes(raw.interactionMode)) {
    throw new Error(`${label}.interactionMode is invalid`);
  }
  const normalized = {
    status: raw.status,
    catalogKey: normalizeReleaseText(raw.catalogKey, `${label}.catalogKey`),
    interactionMode: raw.interactionMode,
    actionExecutionOwner: normalizeReleaseText(
      raw.actionExecutionOwner,
      `${label}.actionExecutionOwner`
    ),
    requiredOnchainCaller: normalizeAddressLower(
      raw.requiredOnchainCaller,
      `${label}.requiredOnchainCaller`
    ),
    certifierCodeHash:
      raw.certifierCodeHash === null
        ? null
        : normalizeBytes32(raw.certifierCodeHash, `${label}.certifierCodeHash`),
    lastRequest: null,
    lastExternalAction: null,
  };
  if (raw.lastRequest !== null) {
    assertExactKeys(raw.lastRequest, `${label}.lastRequest`, [
      "externalRequestId",
      "action",
      "status",
      "operationId",
      "transactionHash",
    ]);
    normalized.lastRequest = {
      externalRequestId: normalizeReleaseText(
        raw.lastRequest.externalRequestId,
        `${label}.lastRequest.externalRequestId`
      ),
      action: normalizeReleaseText(
        raw.lastRequest.action,
        `${label}.lastRequest.action`
      ),
      status: normalizeReleaseText(
        raw.lastRequest.status,
        `${label}.lastRequest.status`
      ),
      operationId: normalizeNullableText(
        raw.lastRequest.operationId,
        `${label}.lastRequest.operationId`
      ),
      transactionHash:
        raw.lastRequest.transactionHash === null
          ? null
          : normalizeBytes32(
              raw.lastRequest.transactionHash,
              `${label}.lastRequest.transactionHash`
            ),
    };
  }
  if (raw.lastExternalAction !== null) {
    assertExactKeys(raw.lastExternalAction, `${label}.lastExternalAction`, [
      "operationType",
      "eventName",
      "status",
      "transactionHash",
      "confirmedBlock",
      "canonical",
    ]);
    if (typeof raw.lastExternalAction.canonical !== "boolean") {
      throw new Error(`${label}.lastExternalAction.canonical must be boolean`);
    }
    normalized.lastExternalAction = {
      operationType: normalizeReleaseText(
        raw.lastExternalAction.operationType,
        `${label}.lastExternalAction.operationType`
      ),
      eventName: normalizeReleaseText(
        raw.lastExternalAction.eventName,
        `${label}.lastExternalAction.eventName`
      ),
      status: normalizeReleaseText(
        raw.lastExternalAction.status,
        `${label}.lastExternalAction.status`
      ),
      transactionHash: normalizeBytes32(
        raw.lastExternalAction.transactionHash,
        `${label}.lastExternalAction.transactionHash`
      ),
      confirmedBlock: normalizeUintString(
        raw.lastExternalAction.confirmedBlock,
        `${label}.lastExternalAction.confirmedBlock`
      ),
      canonical: raw.lastExternalAction.canonical,
    };
  }
  return normalized;
};

const normalizeLifecycle = (raw) => {
  const label = "lifecycle";
  assertExactKeys(raw, label, [
    "currentYear",
    "currentStage",
    "stages",
    "years",
  ]);
  const normalizeYear = (value, field) => {
    if (!Number.isSafeInteger(value) || value < 1 || value > 20) {
      throw new Error(`${field} must be an integer from 1 to 20`);
    }
    return value;
  };
  if (!Array.isArray(raw.stages) || !Array.isArray(raw.years)) {
    throw new Error(`${label}.stages and ${label}.years must be arrays`);
  }
  let currentStage = null;
  if (raw.currentStage !== null) {
    assertExactFields(raw.currentStage, `${label}.currentStage`, [
      "year",
      "stage",
    ]);
    currentStage = {
      year: normalizeYear(raw.currentStage.year, `${label}.currentStage.year`),
      stage: normalizeSafePositiveInteger(
        raw.currentStage.stage + 1,
        `${label}.currentStage.stage`
      ) - 1,
    };
    if (currentStage.stage > 14) {
      throw new Error(`${label}.currentStage.stage is out of range`);
    }
  }
  return {
    currentYear:
      raw.currentYear === null
        ? null
        : normalizeYear(raw.currentYear, `${label}.currentYear`),
    currentStage,
    stages: raw.stages,
    years: raw.years,
  };
};

const RELAY_BALANCE_FIELDS = Object.freeze([
  "projectAddress",
  "receiptAddress",
  "tokenId",
  "year",
  "stage",
  "accruedUnits",
  "relayedUnits",
  "pendingUnits",
]);

const normalizeRelayBalance = (raw, label) => {
  assertExactFields(raw, label, RELAY_BALANCE_FIELDS);
  if (!Number.isSafeInteger(raw.year) || raw.year < 1 || raw.year > 20) {
    throw new Error(`${label}.year is out of range`);
  }
  if (!Number.isSafeInteger(raw.stage) || raw.stage < 0 || raw.stage > 14) {
    throw new Error(`${label}.stage is out of range`);
  }
  return {
    projectAddress: normalizeAddressLower(
      raw.projectAddress,
      `${label}.projectAddress`
    ),
    receiptAddress: normalizeAddressLower(
      raw.receiptAddress,
      `${label}.receiptAddress`
    ),
    tokenId: normalizeUintString(raw.tokenId, `${label}.tokenId`),
    year: raw.year,
    stage: raw.stage,
    accruedUnits: normalizeUintString(
      raw.accruedUnits,
      `${label}.accruedUnits`
    ),
    relayedUnits: normalizeUintString(
      raw.relayedUnits,
      `${label}.relayedUnits`
    ),
    pendingUnits: normalizeUintString(
      raw.pendingUnits,
      `${label}.pendingUnits`
    ),
  };
};

export const normalizeReceiptRelayOverview = (raw) => {
  const label = "receipt relay overview";
  assertExactKeys(raw, label, [
    "protocolVersion",
    "releaseTag",
    "chainId",
    "relayAddress",
    "stakingDistributorAddress",
    "keeperAddress",
    "pendingUnits",
    "lastRelayBlock",
    "lastRelayAt",
    "hasMore",
    "nextCursor",
    "maxTokenIdsPerRelay",
    "pendingBalances",
    "items",
  ]);
  if (
    raw.protocolVersion !== "V2" ||
    raw.releaseTag !== "carbon-protocol-v2-2.0.0"
  ) {
    throw new Error(`${label} is not the current V2 protocol`);
  }
  if (
    !Array.isArray(raw.pendingBalances) ||
    !Array.isArray(raw.items) ||
    typeof raw.hasMore !== "boolean" ||
    !Number.isSafeInteger(raw.maxTokenIdsPerRelay) ||
    raw.maxTokenIdsPerRelay < 1 ||
    raw.maxTokenIdsPerRelay > 64
  ) {
    throw new Error(`${label} balance and item collections must be arrays`);
  }
  return {
    protocolVersion: "V2",
    releaseTag: raw.releaseTag,
    chainId: normalizeUintString(raw.chainId, `${label}.chainId`),
    relayAddress: normalizeAddressLower(raw.relayAddress, `${label}.relayAddress`),
    stakingDistributorAddress: normalizeAddressLower(
      raw.stakingDistributorAddress,
      `${label}.stakingDistributorAddress`
    ),
    keeperAddress: normalizeAddressLower(raw.keeperAddress, `${label}.keeperAddress`),
    pendingUnits:
      raw.pendingUnits === null
        ? null
        : normalizeUintString(raw.pendingUnits, `${label}.pendingUnits`),
    lastRelayBlock:
      raw.lastRelayBlock === null
        ? null
        : normalizeUintString(raw.lastRelayBlock, `${label}.lastRelayBlock`),
    lastRelayAt: normalizeNullableText(raw.lastRelayAt, `${label}.lastRelayAt`),
    hasMore: raw.hasMore,
    nextCursor:
      raw.nextCursor === null
        ? null
        : normalizeUintString(raw.nextCursor, `${label}.nextCursor`),
    maxTokenIdsPerRelay: raw.maxTokenIdsPerRelay,
    pendingBalances: raw.pendingBalances.map((item, index) =>
      normalizeRelayBalance(item, `${label}.pendingBalances[${index}]`)
    ),
    items: raw.items.map((item, index) => {
      const itemLabel = `${label}.items[${index}]`;
      assertExactKeys(item, itemLabel, [
        ...RELAY_BALANCE_FIELDS,
        "relayId",
        "chainId",
        "status",
        "submissionStatus",
        "confirmationStatus",
        "reconciliationStatus",
        "terminalState",
        "emissionKey",
        "stakingRoundId",
        "operationId",
        "transactionHash",
        "blockNumber",
        "retryCount",
        "lastError",
        "createdAt",
        "updatedAt",
      ]);
      if (
        !Number.isSafeInteger(item.relayId) ||
        item.relayId < 1 ||
        !Number.isSafeInteger(item.retryCount) ||
        item.retryCount < 0
      ) {
        throw new Error(`${itemLabel} identifiers are invalid`);
      }
      const balance = normalizeRelayBalance(
        Object.fromEntries(
          RELAY_BALANCE_FIELDS.map((field) => [field, item[field]])
        ),
        itemLabel
      );
      return {
        ...balance,
        relayId: item.relayId,
        chainId: normalizeUintString(item.chainId, `${itemLabel}.chainId`),
        status: normalizeReleaseText(item.status, `${itemLabel}.status`),
        submissionStatus: normalizeReleaseText(
          item.submissionStatus,
          `${itemLabel}.submissionStatus`
        ),
        confirmationStatus: normalizeReleaseText(
          item.confirmationStatus,
          `${itemLabel}.confirmationStatus`
        ),
        reconciliationStatus: normalizeReleaseText(
          item.reconciliationStatus,
          `${itemLabel}.reconciliationStatus`
        ),
        terminalState: normalizeReleaseText(
          item.terminalState,
          `${itemLabel}.terminalState`
        ),
        emissionKey:
          item.emissionKey === null
            ? null
            : normalizeBytes32(item.emissionKey, `${itemLabel}.emissionKey`),
        stakingRoundId:
          item.stakingRoundId === null
            ? null
            : normalizeBytes32(
                item.stakingRoundId,
                `${itemLabel}.stakingRoundId`
              ),
        operationId: normalizeNullableText(
          item.operationId,
          `${itemLabel}.operationId`
        ),
        transactionHash:
          item.transactionHash === null
            ? null
            : normalizeBytes32(
                item.transactionHash,
                `${itemLabel}.transactionHash`
              ),
        blockNumber:
          item.blockNumber === null
            ? null
            : normalizeUintString(item.blockNumber, `${itemLabel}.blockNumber`),
        retryCount: item.retryCount,
        lastError: normalizeNullableText(item.lastError, `${itemLabel}.lastError`),
        createdAt: normalizeNullableText(item.createdAt, `${itemLabel}.createdAt`),
        updatedAt: normalizeNullableText(item.updatedAt, `${itemLabel}.updatedAt`),
      };
    }),
  };
};

export const normalizeReceiptRelayNowResponse = (raw) => {
  const label = "receipt relay submit response";
  assertExactFields(raw, label, ["operationId", "status", "transactionHash"]);
  if (raw.status !== "SUBMITTED") {
    throw new Error(`${label}.status must be SUBMITTED`);
  }
  return {
    operationId: normalizeReleaseText(raw.operationId, `${label}.operationId`),
    status: "SUBMITTED",
    transactionHash: normalizeBytes32(
      raw.transactionHash,
      `${label}.transactionHash`
    ),
  };
};

export const normalizeCompensationClaimablesResponse = (raw) => {
  assertPlainObject(raw, "compensation claimables response");
  assertNoResponseAliasFields(raw, "compensation claimables response");
  if (!Array.isArray(raw.claimables)) {
    throw new Error("compensation claimables response.claimables must be an array");
  }
  return {
    claimables: raw.claimables.map((claim, index) => {
      const label = `compensation claimables response.claimables[${index}]`;
      assertPlainObject(claim, label);
      assertNoResponseAliasFields(claim, label);
      if (!Number.isInteger(claim.year) || claim.year < 1 || claim.year > 20) {
        throw new Error(`${label}.year must be an integer from 1 to 20`);
      }
      if (
        !Number.isInteger(claim.stageIndex) ||
        claim.stageIndex < 0 ||
        claim.stageIndex > 14
      ) {
        throw new Error(`${label}.stageIndex must be an integer from 0 to 14`);
      }
      if (
        !Number.isInteger(claim.stablecoinDecimals) ||
        claim.stablecoinDecimals < 0 ||
        claim.stablecoinDecimals > 255
      ) {
        throw new Error(`${label}.stablecoinDecimals must be a uint8`);
      }
      if (typeof claim.canClaim !== "boolean") {
        throw new Error(`${label}.canClaim must be a boolean`);
      }
      return {
        projectAddress: normalizeAddressLower(
          claim.projectAddress,
          `${label}.projectAddress`
        ),
        planHash: normalizeBytes32(claim.planHash, `${label}.planHash`),
        year: claim.year,
        stageIndex: claim.stageIndex,
        claimableAmount: normalizeUintString(
          claim.claimableAmount,
          `${label}.claimableAmount`
        ),
        stageAllocation: normalizeUintString(
          claim.stageAllocation,
          `${label}.stageAllocation`
        ),
        compensationPaid: normalizeUintString(
          claim.compensationPaid,
          `${label}.compensationPaid`
        ),
        stablecoinAddress: normalizeAddressLower(
          claim.stablecoinAddress,
          `${label}.stablecoinAddress`
        ),
        stablecoinDecimals: claim.stablecoinDecimals,
        canClaim: claim.canClaim,
      };
    }),
  };
};

const normalizeHexBytes = (value, label) => {
  if (
    typeof value !== "string" ||
    !/^0x([0-9a-fA-F]{2})*$/.test(value)
  ) {
    throw new Error(`${label} must be hex byte data`);
  }
  return value;
};

const canonicalJson = (value) =>
  JSON.stringify(
    Object.keys(value)
      .sort()
      .reduce((acc, key) => {
        acc[key] = value[key];
        return acc;
      }, {})
  );

const transactionRequestHash = ({ chainId, from, to, data, value, gas }) =>
  ethers.utils.sha256(
    ethers.utils.toUtf8Bytes(
      canonicalJson({
        chainId: String(chainId),
        from: normalizeAddress(from, "direct-wallet from address").toLowerCase(),
        to: normalizeAddress(to, "direct-wallet to address").toLowerCase(),
        data: normalizeHexBytes(data, "direct-wallet transaction data").toLowerCase(),
        ...(gas === undefined ? {} : { gas }),
        value: String(value),
      })
    )
  );

const normalizeBatchTransactionRequest = (raw, parentLabel) => {
  const label = `${parentLabel}.transactionRequest`;
  assertExactFields(raw, label, [
    "chainId",
    "from",
    "to",
    "data",
    "value",
    "gas",
  ]);
  if (raw.value !== "0") {
    throw new Error(`${label}.value must be zero`);
  }
  if (
    typeof raw.gas !== "string" ||
    !/^0x[1-9a-f][0-9a-f]*$/.test(raw.gas)
  ) {
    throw new Error(`${label}.gas must be canonical hex`);
  }
  const gas = BigInt(raw.gas);
  if (gas > MAX_BATCH_INVESTMENT_GAS) {
    throw new Error(`${label}.gas exceeds 0x1e8480`);
  }
  return Object.freeze({
    chainId: normalizeBoundedUintString(raw.chainId, `${label}.chainId`, {
      allowZero: false,
    }),
    from: normalizeAddress(raw.from, `${label}.from`),
    to: normalizeAddress(raw.to, `${label}.to`),
    data: normalizeHexBytes(raw.data, `${label}.data`).toLowerCase(),
    value: "0",
    gas: raw.gas,
  });
};

export const normalizeStablecoinAllowanceResponse = (raw) => {
  assertPlainObject(raw, "stablecoin allowance response");
  assertNoResponseAliasFields(raw, "stablecoin allowance response");
  return {
    ownerAddress: normalizeAddress(raw.ownerAddress, "ownerAddress"),
    stablecoinAddress: normalizeAddress(raw.stablecoinAddress, "stablecoinAddress"),
    spenderAddress: normalizeAddress(raw.spenderAddress, "spenderAddress"),
    allowance: normalizeUintString(raw.allowance, "allowance"),
  };
};

export const normalizeStablecoinApprovalPrepareResponse = (raw) => {
  assertPlainObject(raw, "stablecoin approval prepare response");
  assertNoResponseAliasFields(raw, "stablecoin approval prepare response");
  if (raw.approvalRequired === false) {
    if (!EXECUTION_MODES.includes(raw.executionMode)) {
      throw new Error(`unknown execution mode: ${raw.executionMode}`);
    }
    return {
      approvalRequired: false,
      executionMode: raw.executionMode,
      businessWalletAddress: normalizeAddress(
        raw.businessWalletAddress,
        "businessWalletAddress"
      ),
      stablecoinAddress: normalizeAddress(raw.stablecoinAddress, "stablecoinAddress"),
      spenderAddress: normalizeAddress(raw.spenderAddress, "spenderAddress"),
      allowance: normalizeUintString(raw.allowance, "allowance"),
      requiredAmount: normalizeUintString(raw.requiredAmount, "requiredAmount"),
    };
  }
  return normalizePrepareResponse(raw);
};

export const normalizeReceiptApprovalPrepareResponse = (raw) => {
  assertPlainObject(raw, "receipt approval prepare response");
  assertNoResponseAliasFields(raw, "receipt approval prepare response");
  if (raw.approvalRequired === false) {
    if (!EXECUTION_MODES.includes(raw.executionMode)) {
      throw new Error(`unknown execution mode: ${raw.executionMode}`);
    }
    if (typeof raw.approved !== "boolean") {
      throw new Error("approved must be a boolean");
    }
    return {
      approvalRequired: false,
      executionMode: raw.executionMode,
      businessWalletAddress: normalizeAddress(
        raw.businessWalletAddress,
        "businessWalletAddress"
      ),
      receiptAddress: normalizeAddress(raw.receiptAddress, "receiptAddress"),
      operatorAddress: normalizeAddress(raw.operatorAddress, "operatorAddress"),
      approved: raw.approved,
    };
  }
  return normalizePrepareResponse(raw);
};

export const normalizeMarketplaceListingResponse = (raw) => {
  assertPlainObject(raw, "marketplace listing response");
  assertNoResponseAliasFields(raw, "marketplace listing response");
  return {
    marketplaceAddress: normalizeAddressLower(
      raw.marketplaceAddress,
      "marketplaceAddress"
    ),
    listingId: normalizeUintString(raw.listingId, "listingId"),
    seller: normalizeAddressLower(raw.seller, "seller"),
    project: normalizeAddressLower(raw.project, "project"),
    receipt: normalizeAddressLower(raw.receipt, "receipt"),
    receiptId: normalizeUintString(raw.receiptId, "receiptId"),
    unitsRemaining: normalizeUintString(raw.unitsRemaining, "unitsRemaining"),
    unitsSold: normalizeUintString(raw.unitsSold, "unitsSold"),
    unitPrice: normalizeUintString(raw.unitPrice, "unitPrice"),
    allowedBuyer: normalizeAddressLower(raw.allowedBuyer, "allowedBuyer"),
    expiresAt: normalizeUintString(String(raw.expiresAt), "expiresAt"),
    status: Number(raw.status),
  };
};

export const normalizeMarketplaceListingsResponse = (raw) => {
  assertPlainObject(raw, "marketplace listings response");
  assertNoResponseAliasFields(raw, "marketplace listings response");
  const marketplaceAddress = normalizeAddressLower(
    raw.marketplaceAddress,
    "marketplaceAddress"
  );
  const listings = Array.isArray(raw.listings) ? raw.listings : [];
  return {
    marketplaceAddress,
    start:
      raw.start === undefined || raw.start === null
        ? undefined
        : normalizeUintString(String(raw.start), "start"),
    nextListingId:
      raw.nextListingId === undefined
        ? undefined
        : normalizeUintString(String(raw.nextListingId), "nextListingId"),
    listings: listings.map((listing) =>
      normalizeMarketplaceListingResponse({
        marketplaceAddress,
        ...listing,
      })
    ),
  };
};

const normalizeTransactionRequest = (raw) => {
  assertPlainObject(raw, "transaction package");
  assertNoResponseAliasFields(raw, "transaction package");
  if (!raw.from || !raw.to || !raw.data) {
    throw new Error("direct-wallet transaction package is incomplete");
  }
  if (raw.value === undefined || raw.value === null) {
    throw new Error("direct-wallet transaction package missing value");
  }
  const value = String(raw.value);
  if (value !== "0") {
    throw new Error("direct-wallet transaction value must be zero");
  }
  const normalized = {
    from: normalizeAddress(raw.from, "direct-wallet from address"),
    to: normalizeAddress(raw.to, "direct-wallet to address"),
    data: normalizeHexBytes(raw.data, "direct-wallet transaction data"),
    value,
  };
  if (raw.gas !== undefined) {
    const batch = normalizeBatchTransactionRequest(
      raw,
      "direct-wallet"
    );
    return batch;
  }
  if (raw.chainId !== undefined) {
    normalized.chainId = normalizeBoundedUintString(
      String(raw.chainId),
      "direct-wallet transaction chainId",
      { allowZero: false }
    );
  }
  return Object.freeze(normalized);
};

export const normalizePrepareResponse = (raw) => {
  assertPlainObject(raw, "prepare response");
  assertNoResponseAliasFields(raw, "prepare response");
  if (raw.state !== undefined) {
    throw new Error("prepare response must use operationState");
  }
  if (!OPERATION_STATES.includes(raw.operationState)) {
    throw new Error(`unknown operation state: ${raw.operationState}`);
  }
  const normalized = normalizeProtocolOperationResponse({
    ...raw,
    state: raw.operationState,
  });
  return normalizePreparedOperationShape(normalized, raw);
};

const normalizePreparedOperationShape = (normalized, raw) => {
  if (normalized.executionMode === "METAKEEP_BUSINESS_WALLET") {
    const consentToken = raw.consentToken;
    if (!consentToken && !isTerminalOperationState(normalized.state)) {
      throw new Error("MetaKeep prepare response missing consent token");
    }
    if (consentToken) {
      normalized.consentToken = consentToken;
    }
  }
  if (normalized.executionMode === "DIRECT_WALLET") {
    if (!DIRECT_WALLET_OPERATION_TYPES.includes(normalized.operationType)) {
      throw new Error(
        `unsupported direct-wallet operation type: ${normalized.operationType}`
      );
    }
    if (normalized.state !== "READY_TO_SIGN") {
      throw new Error("direct-wallet prepare response must be READY_TO_SIGN");
    }
    const tx = raw.transactionRequest;
    normalized.transactionRequest = normalizeTransactionRequest(tx);
    if (normalized.chainId === undefined || normalized.chainId === null) {
      throw new Error("direct-wallet prepare response missing chainId");
    }
    normalized.calldataHash = normalizeBytes32(
      normalized.calldataHash,
      "calldataHash"
    );
    normalized.transactionRequestHash = normalizeBytes32(
      normalized.transactionRequestHash,
      "transactionRequestHash"
    );
    const expectedHash = transactionRequestHash({
      chainId: normalized.chainId,
      ...normalized.transactionRequest,
    });
    if (
      normalized.transactionRequest.chainId !== undefined &&
      normalized.transactionRequest.chainId !== String(normalized.chainId)
    ) {
      throw new Error("direct-wallet transaction chainId mismatch");
    }
    if (normalized.transactionRequestHash !== expectedHash) {
      throw new Error("direct-wallet transactionRequestHash mismatch");
    }
  }
  if (normalized.executionMode === "SAFE_TIMELOCK_EXTERNAL") {
    if (raw.transactionRequest || raw.consentToken) {
      throw new Error("external governance operations are not frontend executable");
    }
  }
  return normalized;
};

export const normalizePreparedOperationResponse = (raw) => {
  assertPlainObject(raw, "prepared operation response");
  if (raw.operationState !== undefined) {
    return normalizePrepareResponse(raw);
  }
  if (raw.state === undefined) {
    throw new Error("prepared operation response missing state");
  }
  const normalized = normalizeProtocolOperationResponse(raw);
  return normalizePreparedOperationShape(normalized, raw);
};

export const isTerminalOperationState = (state) =>
  TERMINAL_OPERATION_STATES.includes(state);
